// Mirrors app/Support/GeoFence.php on the API — kept in sync so the
// in/out-of-zone label a tradie sees client-side (advisory only, never
// gates clocking) matches what the server records as within_zone_in/out.

function pointInPolygon(lat, lng, ring) {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    const intersect =
      yi > lat !== yj > lat &&
      lng < ((xj - xi) * (lat - yi)) / (yj - yi || 1e-12) + xi
    if (intersect) inside = !inside
  }
  return inside
}

function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000
  const toRad = (d) => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

const isPosition = (v) =>
  Array.isArray(v) && v.length >= 2 && isFinite(v[0]) && isFinite(v[1])

const isRing = (v) => Array.isArray(v) && v.length >= 3 && v.every(isPosition)

// A Polygon's coordinates are an array of linear rings. Some zones were
// stored with a single ring directly ([[x,y],...] instead of [[[x,y],...]]),
// which makes readers treat a longitude as a ring — that is what threw
// "Invalid LatLng object: (undefined, undefined)" when drawing the map.
function normalizePolygonCoordinates(coords) {
  if (isRing(coords[0])) return coords.every(isRing) ? coords : null
  if (isRing(coords)) return [coords]
  return null
}

// Mirrors GeoJson::geometry() in app/Support/GeoJson.php on the API.
// Reads both the canonical Feature shape and the bare-geometry shape older
// zones were stored in, so a row written before the API normalized geo_json
// still draws and still matches. Returns null when unusable — callers treat
// that as "no geofence" rather than throwing.
export function geoJsonGeometry(geoJson) {
  if (!geoJson) return null

  let geometry = geoJson.geometry
  if (!geometry && geoJson.type && geoJson.coordinates) geometry = geoJson

  if (!geometry || !geometry.type || !Array.isArray(geometry.coordinates)) {
    return null
  }

  let coordinates = geometry.coordinates
  if (coordinates.length === 0) return null

  if (geometry.type === 'Polygon') {
    coordinates = normalizePolygonCoordinates(coordinates)
    if (!coordinates) return null
  }

  if (geometry.type === 'Point' && !isPosition(coordinates)) return null

  return { type: geometry.type, coordinates }
}

// Rewrites either shape into the Feature shape Leaflet's L.geoJSON expects.
export function toGeoJsonFeature(geoJson) {
  const geometry = geoJsonGeometry(geoJson)
  if (!geometry) return null
  return { type: 'Feature', geometry, properties: {} }
}

// zone: { id, zone_name, geo_json: { geometry: { type, coordinates } }, radius }
export function zoneContains(zone, lat, lng) {
  const geometry = geoJsonGeometry(zone && zone.geo_json)
  if (!geometry) return false

  if (geometry.type === 'Polygon') {
    return pointInPolygon(lat, lng, geometry.coordinates[0])
  }
  if (geometry.type === 'Point' && zone.radius != null) {
    const [centerLng, centerLat] = geometry.coordinates
    return haversineMeters(lat, lng, centerLat, centerLng) <= zone.radius
  }
  return false
}

// Returns { label, zoneId } for the informational in/out-of-zone line.
// Never used to gate clock in/out — matches API/Flutter's advisory-only
// treatment of geofence containment.
export function geofenceLabel(zones, lat, lng) {
  if (!zones || zones.length === 0) {
    return { label: 'No zones on this project', zoneId: null }
  }
  if (lat == null || lng == null) {
    return { label: 'Location unavailable', zoneId: null }
  }
  const match = zones.find((z) => zoneContains(z, lat, lng))
  if (match) {
    return { label: `Inside ${match.zone_name}`, zoneId: match.id }
  }
  return { label: 'Outside site zones', zoneId: null }
}

// Best-effort browser geolocation fetch — resolves { lat, lng } or null.
// Never rejects: permission denial, timeout, or an unsupported browser all
// resolve to null so callers can proceed with clock in/out regardless.
export function tryGetPosition({ timeoutMs = 8000 } = {}) {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null)
      return
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { timeout: timeoutMs, maximumAge: 60000 },
    )
  })
}
